import { Box, Tooltip, Typography } from '@mui/material';
import type { ReactElement } from 'react';
import type { FieldVersionHistory } from '@/hooks/useVersionHistory';
import { formatDateAsDayMonthYear } from '@/utils/dateFormatter';
import { formatTimestamp, titleize } from '@/utils/helpers';

type VersionTooltipProps = {
  fieldHistory: FieldVersionHistory | undefined;
  children: ReactElement;
  fieldType?: string;
};

/**
 * Tooltip component that displays version history for a field
 * Shows the latest change with author, date, and old value
 */
export const VersionTooltip = ({ fieldHistory, fieldType, children }: VersionTooltipProps) => {
  if (!fieldHistory || !fieldHistory.latestChange) {
    return children;
  }

  const { latestChange } = fieldHistory;
  const formatOldValue = (value: unknown): string => {
    if (value === null || value === '') {
      return 'empty';
    }

    switch (fieldType) {
      case 'date':
        return formatDateAsDayMonthYear(value as string);
      case 'select':
        return titleize(value);
      default:
        return String(value);
    }
  };

  const tooltipContent = (
    <Box sx={{ p: 0.5 }}>
      <Typography variant='body2' sx={{ fontWeight: 'bold', mb: 0.5 }}>
        Last Changed
      </Typography>
      <Typography variant='caption' component='div'>
        By: {latestChange.changedBy}
      </Typography>
      <Typography variant='caption' component='div'>
        Date: {formatTimestamp(latestChange.changedAt)}
      </Typography>
      <Typography variant='caption' component='div' sx={{ mt: 0.5 }}>
        Previous value: {formatOldValue(latestChange.oldValue)}
      </Typography>
      {fieldHistory.allChanges.length > 1 && (
        <Typography variant='caption' component='div' sx={{ mt: 0.5, fontStyle: 'italic' }}>
          ({fieldHistory.allChanges.length} total changes)
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} placement='top' arrow enterDelay={300} leaveDelay={200}>
      {children}
    </Tooltip>
  );
};
